import { JSX, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import { useIPE } from 'src/redux/slices/Interview/hooks';
import { MAX_QIT_IDS_FROM_PARAMS } from 'src/redux/slices/Interview/types';
import { useDataVault } from 'src/contexts/DataVaultContext';
import LoadingScreen from './screens/LoadingScreen';
import InterviewShell from './InterviewShell';

/**
 * DataVaultInterviewRoute — boots the existing interview engine for a PQ
 * interview launched from the Data Vault.
 *
 * Handles two entry points:
 * - Full survey ("Continue" button): no sectionId in URL, backPath = data-vault landing
 * - Section re-interview ("Answer these questions again"): sectionId in URL, backPath = section detail
 *
 * Differences from InterviewRoute:
 * - surveyId comes from DataVaultContext (profile survey), not a URL param
 * - No data-vault-transfer guard (not applicable to profile surveys)
 * - backPath is injected into Redux so InterviewHeader + completion screens
 *   navigate back to the correct Data Vault page instead of /renter
 */
export default function DataVaultInterviewRoute(): JSX.Element | null {
  const { sectionId } = useParams<{ sectionId: string }>();
  const [searchParams] = useSearchParams();
  const { surveyId, actingOnMemberId } = useDataVault();
  const dispatch = useDispatch();
  const { callIPE } = useIPE();

  const qitsParam = searchParams.get('qits');
  const onlyQitIds: number[] = qitsParam
    ? qitsParam
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n))
        .slice(0, MAX_QIT_IDS_FROM_PARAMS)
    : [];

  const backPath = sectionId
    ? actingOnMemberId
      ? `/renter/data-vault/members/${actingOnMemberId}/pq/sections/${sectionId}`
      : `/renter/data-vault/pq/sections/${sectionId}`
    : actingOnMemberId
    ? `/renter/data-vault/members/${actingOnMemberId}`
    : '/renter/data-vault';

  useEffect(() => {
    if (!surveyId) {
      return;
    }
    dispatch(
      interviewActions.initInterview({
        surveyId,
        onBehalfOf: actingOnMemberId,
        onlyQitIds: onlyQitIds.length > 0 ? onlyQitIds : null,
        backPath,
      })
    );
    callIPE({
      surveyIdOverride: surveyId,
      onBehalfOfOverride: actingOnMemberId,
      onlyQitIds: onlyQitIds.length > 0 ? onlyQitIds : undefined,
    });
    return () => {
      dispatch(interviewActions.resetInterview());
    };
    // [surveyId] is the boot trigger. actingOnMemberId and backPath are stable
    // for the lifetime of this route (set once by DataVaultLayout on mount);
    // including them would cause a redundant re-boot on minor context ticks.
  }, [surveyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Profile survey is still loading — DataVaultLayout fetches it on mount.
  if (!surveyId) {
    return <LoadingScreen />;
  }

  return <InterviewShell />;
}
